import axios from "axios";
import { useMutation, useQueryClient } from "react-query";
import { nuclearn_api_url } from "src/App";
import { LINKED_PARTS_QUERY_KEY } from "./PartService";

// ===========================
// Types
// ===========================

export interface PartsSearchDialogue {
  id: number;
  dialogue_id: number;
  create_user_fk: number;
  create_date: string | null;
}

export interface CreatePartsSearchPayload {
  /**
   * Pre-created parts-ai dialogue to attach the search to. Omit to have the
   * server create the dialogue and link the search atomically — preferred.
   */
  dialogue_id?: number | null;
  /**
   * Chat config / persona to anchor the newly-created dialogue to. Drives
   * `ranking_type` on streamed assistant messages, which the frontend gates
   * the thumbs-up / thumbs-down UI on. Ignored when `dialogue_id` is supplied.
   */
  chat_config_id?: number | null;
}

// ===========================
// API
// ===========================

// Read at call-time to avoid TDZ issues during circular imports.
const searchesUrl = () => `${nuclearn_api_url}/products/parts/searches`;

const createPartsSearch = async (
  payload: CreatePartsSearchPayload,
): Promise<PartsSearchDialogue> => {
  const response = await axios.post<PartsSearchDialogue>(
    searchesUrl(),
    payload,
  );
  return response.data;
};

// ===========================
// React Query Hooks
// ===========================

export const useCreatePartsSearch = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: CreatePartsSearchPayload = {}) => createPartsSearch(payload),
    {
      onSuccess: (data) => {
        if (
          queryClient.getQueryData([
            LINKED_PARTS_QUERY_KEY,
            data.dialogue_id,
          ]) === undefined
        ) {
          queryClient.setQueryData(
            [LINKED_PARTS_QUERY_KEY, data.dialogue_id],
            [],
          );
        }
      },
    },
  );
};
